import { type FederalDistrictItem } from '@entities/federal-district'
import { type FulfillmentRequestApi } from '@entities/fulfillment-request'

import { type WizardContact, type WizardValues } from '../model/types'

export const emptyWizardValues: WizardValues = {
  title: '',
  shipmentTypes: [],
  marketplaces: [],
  marketplaceOtherName: '',
  cities: [],
  federalDistricts: [],
  warehouses: [],
  services: [],
  subjectId: null,
  subjectName: '',
  productUrl: '',
  productSize: '',
  packageSize: '',
  unitCount: '',
  dueDate: '',
  description: '',
  packingType: null,
  packingComment: '',
  needsSpecialPacking: false,
  specialPacking: '',
  needsTwiceADay: false,
  requirements: '',
  phone: '',
  contacts: [],
}

export const snapshotToWizardValues = (snapshot: FulfillmentRequestApi.EditSnapshot): WizardValues => ({
  title: snapshot.title,
  shipmentTypes: [...snapshot.shipment_types],
  marketplaces: [...snapshot.marketplaces],
  marketplaceOtherName: snapshot.marketplace_other_name ?? '',
  cities: snapshot.cities.map((city) => ({
    name: city.name,
    regionName: city.region_name,
    isPriority: city.is_priority,
  })),
  federalDistricts: [...snapshot.federal_districts],
  warehouses: snapshot.warehouses.map((warehouse) => ({ ...warehouse })),
  services: [...snapshot.services],
  subjectId: snapshot.subject_id,
  subjectName: snapshot.subject_name ?? '',
  productUrl: snapshot.product_url ?? '',
  productSize: snapshot.product_size ?? '',
  packageSize: snapshot.package_size ?? '',
  unitCount: snapshot.unit_count === null ? '' : String(snapshot.unit_count),
  dueDate: snapshot.due_date ?? '',
  description: snapshot.description ?? '',
  packingType: snapshot.packing_type,
  packingComment: snapshot.packing_comment ?? '',
  needsSpecialPacking: snapshot.special_packing !== null,
  specialPacking: snapshot.special_packing ?? '',
  needsTwiceADay: snapshot.needs_twice_a_day,
  requirements: snapshot.requirements ?? '',
  phone: snapshot.phone,
  contacts: snapshot.contacts.map((contact) => ({ ...contact })),
})

const hasFastShipment = (shipmentTypes: FulfillmentRequestApi.ShipmentType[]) =>
  shipmentTypes.includes('fbs') || shipmentTypes.includes('dbs')

const needsFederalDistricts = (shipmentTypes: FulfillmentRequestApi.ShipmentType[]) => shipmentTypes.includes('fbs')

export const toSaveRequest = (
  values: WizardValues,
  federalDistricts: readonly FederalDistrictItem[] | undefined
): FulfillmentRequestApi.SaveRequest => ({
  title: values.title.trim(),
  shipment_types: values.shipmentTypes,
  marketplaces: values.marketplaces,
  // Название нужно только вместе с площадкой «другая»: иначе бэкенд его всё равно отбросит.
  marketplace_other_name: values.marketplaces.includes('other') ? values.marketplaceOtherName.trim() : null,
  cities: values.cities.map((city) => ({
    name: city.name,
    region_name: city.regionName,
    is_priority: city.isPriority,
  })),
  // Выбор храним на случай возврата к FBS, но без FBS округа не должны влиять на подбор заявки.
  federal_districts: needsFederalDistricts(values.shipmentTypes)
    ? federalDistricts
      ? values.federalDistricts.filter((code) => federalDistricts.some((district) => district.code === code))
      : values.federalDistricts
    : [],
  warehouses: values.warehouses,
  services: values.services,
  subject_id: values.subjectId,
  subject_name: values.subjectName.trim() || null,
  product_url: values.productUrl.trim() || null,
  product_size: values.productSize.trim() || null,
  package_size: values.packageSize.trim() || null,
  unit_count: values.unitCount.trim() ? Number(values.unitCount) : null,
  due_date: values.dueDate || null,
  description: values.description.trim() || null,
  packing_type: values.packingType,
  packing_comment: values.packingComment.trim() || null,
  // Галочку снимают вместе с текстом: снятая галочка означает «особая упаковка не нужна».
  special_packing: values.needsSpecialPacking ? values.specialPacking.trim() || null : null,
  // Требование могло остаться от снятой галочки FBS: бэкенд такую заявку не примет.
  needs_twice_a_day: values.needsTwiceADay && hasFastShipment(values.shipmentTypes),
  requirements: values.requirements.trim() || null,
  phone: values.phone.trim(),
  contacts: values.contacts
    .filter((contact: WizardContact) => contact.value.trim())
    .map((contact) => ({ messenger: contact.messenger, value: contact.value.trim() })),
})
